import type { CallLogRowButtonsProps } from './types';
import { IconButton } from 'Components/shared/IconButton';
import type { IconButtonProps } from 'Components/shared/IconButton/types';
import { ListPopover } from 'Components/shared/ListPopover';
import { CALL_STATE } from 'Constants/enums';
import { TESTID_BUTTONS, TESTID_CALL_LOGS } from 'Constants/testids';
import { MobXProviderContext, observer } from 'mobx-react';
import { useCallRouter } from 'Modules/softphone/hooks';
import React from 'react';
import { useNavigate } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { useCallLogSidebarInfo } from './hooks/useCallLogSidebarInfo';

export const CallLogRowButtons: React.FC<CallLogRowButtonsProps> = observer(
  ({ personId, phoneNumber, overrideContact, isSelectionMode = false }) => {
    const {
      conversation: {
        loadOrCreateConversationWithPost,
        LoggedInUserActiveConferenceConversation,
        postConferenceByConversationId,
      },
      person: {
        personAvaliableFeatures,
        getExtrContactByPhoneNumber,
      },
      phoneCall,
    } = React.useContext<RootStoreProps>(MobXProviderContext);

    const { callWithPerson } = useCallRouter();

    const [showMoreOptionsModal, setShowMoreOptionsModal] =
      React.useState(false);
    const [showCallOptionsModal, setShowCallOptionsModal] =
      React.useState(false);
    const [callState, setcallState] = React.useState<CALL_STATE>(
      CALL_STATE.Available
    );
    const navigate = useNavigate();
    const lines = getExtrContactByPhoneNumber(phoneNumber)?.phoneNumbers;
    const hasMultipleCallLines = (lines?.length ?? 0) > 1;

    const { openInfoDetails } = useCallLogSidebarInfo({
      personId,
      phoneNumber,
      overrideContact,
      onCloseMenu: () => setShowMoreOptionsModal(false),
    });

    React.useEffect(() => {
      let nextCallState = CALL_STATE.Available;
      phoneCall.phoneCalls.forEach(({ callUri, isCallConnecting }) => {
        const elementPR = callUri?.split('@')[0].substring(2);
        if (elementPR === personId?.toString()) {
          nextCallState = isCallConnecting
            ? CALL_STATE.Connecting
            : CALL_STATE.Connected;
        }
      });
      setcallState(nextCallState);
    }, []);

    const handleOpenChat = () => {
      loadOrCreateConversationWithPost(
        personId,
        !personId && phoneNumber?.replace(/\D/g, '')
      ).then((resp) => {
        pushToGTMDataLayer('directoryMessageUser');
        navigate(`/chat/conversations/${resp.data.id}/menu`);
      });
    };

    const makeCall = () => {
      if (!personId && !phoneNumber)
        throw new Error('Either Phone Number or Person Id is required.');
      if (personId) {
        void callWithPerson(personId.toString(), undefined);
      } else if (phoneNumber) {
        void callWithPerson(undefined, phoneNumber);
      }
    };

    const makeVideoCall = () => {
      loadOrCreateConversationWithPost(personId, !personId && phoneNumber).then(
        (item) => {
          postConferenceByConversationId(item.data.id.toString());
        }
      );
    };

    const iconButtonProps: Pick<IconButtonProps, 'popupPosition' | 'size'> = {
      popupPosition: 'bottom center',
      size: 'medium',
    };

    const isCallingDisabled = {
      title:
        !isSelectionMode && callState !== CALL_STATE.Available
          ? 'You are currently on a call with this person'
          : '',
      condition: isSelectionMode || callState !== CALL_STATE.Available,
    };

    const callButton = (
      <IconButton
        content="Start call"
        icon="call"
        onClick={hasMultipleCallLines ? () => { } : makeCall}
        disabled={isCallingDisabled}
        testid={TESTID_CALL_LOGS['ROW_BUTTONS'] + TESTID_BUTTONS['CALL']}
        {...iconButtonProps}
      />
    );

    const callLineOptions =
      lines?.map(({ type, number }) => ({
        text: `${type.charAt(0).toUpperCase() + type.slice(1)}: ${number}`,
        testId:
          TESTID_CALL_LOGS['ROW_BUTTONS'] +
          `${TESTID_BUTTONS['CALL']}-${number}`,
        onClick: makeCall,
      })) ?? [];

    const isVideoCallDisabled =
      !!LoggedInUserActiveConferenceConversation ||
      !personAvaliableFeatures.video.enabled;

    const moreOptions = [
      {
        text: 'Video call',
        icon: 'video' as const,
        testId: TESTID_CALL_LOGS['ROW_BUTTONS'] + TESTID_BUTTONS['VIDEO_CALL'],
        disabled: {
          condition: isVideoCallDisabled,
          title: 'Video conference unavailable',
        },
        onClick: makeVideoCall,
      },
      {
        text: 'View contact details',
        icon: 'info' as const,
        testId:
          TESTID_CALL_LOGS['ROW_BUTTONS'] + TESTID_BUTTONS['CONTACT_DETAILS'],
        onClick: openInfoDetails,
      },
    ];

    return (
      <div className="flex flex-row justify-end items-center ml-[17px]">
        {phoneNumber && hasMultipleCallLines ? (
          <ListPopover
            side="bottom"
            align="end"
            options={callLineOptions}
            open={showCallOptionsModal}
            onOpenChange={(open) => {
              if (isCallingDisabled.condition) {
                return;
              }
              setShowCallOptionsModal(open);
            }}
            closeOnScroll
            scrollContainerId="directory-list"
          >
            {callButton}
          </ListPopover>
        ) : (
          callButton
        )}

        <IconButton
          content="Start messaging"
          icon="chat"
          onClick={handleOpenChat}
          disabled={{
            condition: isSelectionMode,
            title: '',
          }}
          testid={TESTID_CALL_LOGS['ROW_BUTTONS'] + TESTID_BUTTONS['MESSAGE']}
          {...iconButtonProps}
        />

        {!personId && phoneNumber ? (
          <IconButton
            content="View contact details"
            icon="info"
            onClick={openInfoDetails}
            disabled={{
              condition: isSelectionMode,
              title: '',
            }}
            testid={
              TESTID_CALL_LOGS['ROW_BUTTONS'] +
              TESTID_BUTTONS['CONTACT_DETAILS']
            }
            {...iconButtonProps}
          />
        ) : (
          <ListPopover
            side="bottom"
            align="end"
            options={moreOptions}
            open={showMoreOptionsModal}
            onOpenChange={(open) => {
              if (isSelectionMode) {
                return;
              }
              setShowMoreOptionsModal(open);
            }}
            closeOnScroll
            scrollContainerId="directory-list"
          >
            <IconButton
              content="More"
              icon="more"
              onClick={() => { }}
              disabled={{
                condition: isSelectionMode,
                title: '',
              }}
              testid={
                TESTID_CALL_LOGS['ROW_BUTTONS'] +
                TESTID_BUTTONS['MORE_OPTIONS']
              }
              {...iconButtonProps}
            />
          </ListPopover>
        )}
      </div>
    );
  }
);
